import { getRenderStoreyCount } from '@shared/helpers/design/building-design-render-prompt.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type {
  MapCameraState,
  MapRenderAiSelection,
  MapRenderRequest,
} from '@shared/models/design/map-render.model';
import type { Project } from '@shared/models/project.model';

/**
 * Build the photorealistic render payload for a report cover image.
 * Screenshot is required for `screenshot` and `screenshot+building-json`.
 */
export const buildReportCoverRenderRequest = (input: {
  project: Project;
  camera: MapCameraState;
  sourceType: MapRenderRequest['sourceType'];
  screenshot?: string;
  prompt?: string;
  ai?: MapRenderAiSelection;
}): MapRenderRequest => {
  const buildingDesign = getBuildingDesignFromProjectOrBuilding(input.project);
  const base = {
    projectId: input.project.id,
    mode: '3d' as const,
    camera: input.camera,
    location: buildingDesign.location,
    storeyCount: getRenderStoreyCount(buildingDesign),
    prompt: input.prompt,
    ai: input.ai ?? { provider: 'gemini' as const },
  };

  switch (input.sourceType) {
    case 'screenshot': {
      if (!input.screenshot) {
        throw new Error('Screenshot required for image geometry format');
      }
      return {
        ...base,
        sourceType: 'screenshot',
        screenshot: input.screenshot,
      };
    }
    case 'screenshot+building-json': {
      if (!input.screenshot) {
        throw new Error('Screenshot required for image + json geometry format');
      }
      return {
        ...base,
        sourceType: 'screenshot+building-json',
        screenshot: input.screenshot,
        buildingDesign,
      };
    }
    case 'building-json':
      return {
        ...base,
        sourceType: 'building-json',
        buildingDesign,
      };
    default:
      return assertUnreachable(input.sourceType);
  }
};

/**
 * HTTP status for GET cover-image when the project has no stored photorealistic cover.
 * Treat as absence, not failure: the report falls back to the live 3D view.
 * Do not use 404 for this case — 404 is reserved for real not-found (e.g. expired generation key).
 */
export const REPORT_COVER_IMAGE_NO_CONTENT_STATUS = 204 as const;

/**
 * True when a cover-image GET completed with no stored image ({@link REPORT_COVER_IMAGE_NO_CONTENT_STATUS}).
 * 200 means a blob is in the body. Other statuses are request failures, not missing.
 */
export const isReportCoverImageMissingStatus = (status: number): boolean =>
  status === REPORT_COVER_IMAGE_NO_CONTENT_STATUS;

/**
 * API path for the persisted report cover image.
 * Public generation/shared keys take precedence over the authenticated project id.
 */
export const getReportCoverImagePath = (input: {
  projectId: number;
  generationKey?: string;
  sharingKey?: string;
}): string => {
  if (input.generationKey) {
    return `projects/generation/${input.generationKey}/report-cover-image`;
  }
  if (input.sharingKey) {
    return `projects/shared/${input.sharingKey}/report-cover-image`;
  }
  return `projects/${input.projectId}/report-cover-image`;
};
